import type { WorldPos, WorldVector3D } from './measureUtils';
import {
  convertZUpToYUp,
  convertVectorZUpToYUp,
} from './measureUtils';

/* ------------------------------------------------------------------ */
/* Types                                                              */
/* ------------------------------------------------------------------ */

export interface CompactIndexSegment {
  segmentIndex: number;
  bbox?: {
    min: [number, number, number];
    max: [number, number, number];
  };
  neighbors: number[];
  imageIds: number[];
  imageCount: number;
  segmentUrl?: string;
}

export interface CompactIndexImage {
  id: number;
  name: string;
  seq: number;
  frame: number;
  cam: number;
  pos: [number, number, number];
  fwd: [number, number, number];
  segments: number[];
  imagePath?: string;
}

export interface CompactImageIndex {
  version?: number;
  source?: {
    manifest?: string;
    poses?: string;
  };
  stats?: {
    segmentCount: number;
    imageCount: number;
    unassignedImageCount?: number;
  };
  segments: CompactIndexSegment[];
  images: CompactIndexImage[];
}

/* ------------------------------------------------------------------ */
/* Runtime-ready enriched types                                       */
/* ------------------------------------------------------------------ */

export interface RuntimeImageRecord extends CompactIndexImage {
  worldPos: WorldPos;
  worldFwd: WorldVector3D;
}

export interface ImageIndexCandidateResult {
  segment: CompactIndexSegment | null;
  candidates: RuntimeImageRecord[];
  candidateIds: number[];
}

/* ------------------------------------------------------------------ */
/* Store                                                               */
/* ------------------------------------------------------------------ */

export class ImageIndexStore {
  readonly rawIndex: CompactImageIndex;
  readonly segments: CompactIndexSegment[];
  readonly images: RuntimeImageRecord[];

  readonly segmentMap: Map<number, CompactIndexSegment>;
  readonly imageMap: Map<number, RuntimeImageRecord>;

  constructor(index: CompactImageIndex) {
    this.rawIndex = index;
    this.segments = index.segments;

    this.segmentMap = new Map(
      index.segments.map((segment) => [segment.segmentIndex, segment])
    );

    this.images = index.images.map((image) => {
      const worldPos = convertZUpToYUp({
        x: image.pos[0],
        y: image.pos[1],
        z: image.pos[2],
      });

      const worldFwd = convertVectorZUpToYUp({
        x: image.fwd[0],
        y: image.fwd[1],
        z: image.fwd[2],
      });

      return {
        ...image,
        worldPos,
        worldFwd,
      };
    });

    this.imageMap = new Map(
      this.images.map((image) => [image.id, image])
    );
  }

  /* ---------------------------------------------------------------- */
  /* Segment access                                                    */
  /* ---------------------------------------------------------------- */

  getSegment(segmentIndex: number): CompactIndexSegment | null {
    return this.segmentMap.get(segmentIndex) ?? null;
  }

  getNeighborSegments(segmentIndex: number): CompactIndexSegment[] {
    const segment = this.getSegment(segmentIndex);
    if (!segment) return [];

    return segment.neighbors
      .map((neighborIndex) => this.segmentMap.get(neighborIndex) ?? null)
      .filter((s): s is CompactIndexSegment => s !== null);
  }

  /* ---------------------------------------------------------------- */
  /* Image access                                                      */
  /* ---------------------------------------------------------------- */

  getImage(imageId: number): RuntimeImageRecord | null {
    return this.imageMap.get(imageId) ?? null;
  }

  getImages(imageIds: number[]): RuntimeImageRecord[] {
    const results: RuntimeImageRecord[] = [];

    for (const imageId of imageIds) {
      const image = this.imageMap.get(imageId);
      if (image) {
        results.push(image);
      }
    }

    return results;
  }

  /* ---------------------------------------------------------------- */
  /* Candidate gathering                                               */
  /* ---------------------------------------------------------------- */

  getCandidateImageIdsForSegment(
    segmentIndex: number,
    includeNeighbors = true
  ): number[] {
    const segment = this.getSegment(segmentIndex);
    if (!segment) return [];

    const ids = new Set<number>();

    for (const imageId of segment.imageIds) {
      ids.add(imageId);
    }

    if (includeNeighbors) {
      for (const neighborIndex of segment.neighbors) {
        const neighbor = this.segmentMap.get(neighborIndex);
        if (!neighbor) continue;

        for (const imageId of neighbor.imageIds) {
          ids.add(imageId);
        }
      }
    }

    return Array.from(ids);
  }

  getCandidateImagesForSegment(
    segmentIndex: number,
    includeNeighbors = true
  ): RuntimeImageRecord[] {
    const candidateIds = this.getCandidateImageIdsForSegment(
      segmentIndex,
      includeNeighbors
    );

    return this.getImages(candidateIds);
  }

  getCandidateImageDataForSegment(
    segmentIndex: number,
    includeNeighbors = true
  ): ImageIndexCandidateResult {
    const segment = this.getSegment(segmentIndex);
    if (!segment) {
      return {
        segment: null,
        candidates: [],
        candidateIds: [],
      };
    }

    const candidateIds = this.getCandidateImageIdsForSegment(
      segmentIndex,
      includeNeighbors
    );

    return {
      segment,
      candidates: this.getImages(candidateIds),
      candidateIds,
    };
  }
}

/* ------------------------------------------------------------------ */
/* Fetch                                                               */
/* ------------------------------------------------------------------ */

export async function fetchImageIndexStore(
  indexUrl: string
): Promise<ImageIndexStore> {
  console.log('[ImageIndexUtils] Fetching image index:', indexUrl);

  const response = await fetch(indexUrl);
  if (!response.ok) {
    throw new Error(
      `[ImageIndexUtils] Failed to fetch image index: ${response.status} ${response.statusText}`
    );
  }

  const data = (await response.json()) as CompactImageIndex;

  console.log(
    '[ImageIndexUtils] Loaded image index:',
    data.stats?.segmentCount ?? data.segments.length,
    'segments,',
    data.stats?.imageCount ?? data.images.length,
    'images'
  );

  return new ImageIndexStore(data);
}